import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { useCMS } from '../context/CMSContext';

const Legal = () => {
  const { content } = useCMS();
  const { company } = content;
  const [searchParams] = useSearchParams();
  const section = searchParams.get('section') || 'legal';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [section]);

  const sections = {
    legal: {
      title: 'Mentions Légales',
      id: 'legal',
    },
    privacy: {
      title: 'Politique de Confidentialité',
      id: 'privacy',
    },
    terms: {
      title: 'Conditions d\'utilisation',
      id: 'terms',
    },
  };

  const currentSection = sections[section] || sections.legal;

  return (
    <div className="min-h-screen py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Navigation Tabs */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-wrap gap-4 mb-8 justify-center"
        >
          {Object.entries(sections).map(([key, sec]) => (
            <a
              key={key}
              href={`#/legal?section=${key}`}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                section === key
                  ? 'bg-pink-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 border border-gray-300 hover:border-pink-600'
              }`}
            >
              {sec.title}
            </a>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-8 shadow-lg"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-8">
            {currentSection.title}
          </h1>
          <div className="space-y-8">
            {currentSection.id === 'legal' && (
              <>
                {/* Company Information */}
                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Informations sur l'entreprise
                  </h2>
                  <div className="bg-gray-50 rounded-lg p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <strong>Raison sociale:</strong> {company.name} SARL
                      </div>
                      <div>
                        <strong>Date de création:</strong> Septembre 2016
                      </div>
                      <div>
                        <strong>Adresse:</strong> {company.address}
                      </div>
                      <div>
                        <strong>Téléphone:</strong> {company.phone}
                      </div>
                      <div>
                        <strong>Email:</strong> {company.email}
                      </div>
                      <div>
                        <strong>N° Contribuable:</strong> {company.taxpayer}
                      </div>
                      <div className="md:col-span-2">
                        <strong>RCCM:</strong> {company.rccm}
                      </div>
                    </div>
                  </div>
                </section>

                {/* Hosting & Technical */}
                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Hébergement et informations techniques
                  </h2>
                  <div className="prose prose-gray max-w-none">
                    <p>
                      Ce site web est hébergé et géré conformément aux
                      réglementations en vigueur au Cameroun et dans la
                      sous-région CEMAC.
                    </p>
                  </div>
                </section>

                {/* Intellectual Property */}
                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Propriété Intellectuelle
                  </h2>
                  <div className="prose prose-gray max-w-none">
                    <p>
                      Tous les contenus de ce site (textes, images, logos,
                      vidéos) sont protégés par le droit d'auteur et
                      appartiennent à QHSE Consulting ou à ses partenaires. Toute
                      reproduction, distribution ou utilisation sans autorisation
                      écrite est strictement interdite.
                    </p>
                  </div>
                </section>
              </>
            )}

            {currentSection.id === 'privacy' && (
              <>
                {/* Privacy Policy */}
                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Collecte des données
                  </h2>
                  <div className="prose prose-gray max-w-none">
                    <p>
                      QHSE Consulting collecte uniquement les données personnelles
                      nécessaires à la fourniture de ses services. Ces données
                      incluent les informations de contact (nom, email, téléphone)
                      et les informations relatives aux demandes de services.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Utilisation des données
                  </h2>
                  <div className="prose prose-gray max-w-none">
                    <p className="mb-4">
                      Les données collectées sont utilisées exclusivement pour :
                    </p>
                    <ul className="list-disc list-inside mb-4 space-y-1">
                      <li>Répondre aux demandes de devis et d'information</li>
                      <li>Fournir nos services de conseil QHSE</li>
                      <li>Communiquer sur nos formations et services</li>
                      <li>
                        Respecter nos obligations légales et réglementaires
                      </li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Protection des données
                  </h2>
                  <div className="prose prose-gray max-w-none">
                    <p>
                      Nous nous engageons à protéger vos données personnelles et à
                      respecter la confidentialité. Aucune donnée n'est transmise à
                      des tiers sans votre consentement explicite, sauf obligation
                      légale.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Vos droits
                  </h2>
                  <div className="prose prose-gray max-w-none">
                    <p>
                      Conformément à la réglementation en vigueur, vous disposez
                      des droits d'accès, de rectification, de suppression et de
                      portabilité de vos données. Pour exercer ces droits,
                      contactez-nous à {company.email}.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Cookies
                  </h2>
                  <div className="prose prose-gray max-w-none">
                    <p>
                      Ce site utilise des cookies pour améliorer votre expérience
                      utilisateur. En continuant à naviguer, vous acceptez notre
                      utilisation de cookies.
                    </p>
                  </div>
                </section>
              </>
            )}

            {currentSection.id === 'terms' && (
              <>
                {/* Terms of Service */}
                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Prestations de services
                  </h2>
                  <div className="prose prose-gray max-w-none">
                    <p>
                      QHSE Consulting fournit des services de conseil en Qualité,
                      Hygiène, Sécurité et Environnement. Nos prestations incluent
                      l'accompagnement à la certification ISO, la formation,
                      l'assainissement, le contrôle pollution et les services
                      d'hygiène.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Engagements
                  </h2>
                  <div className="prose prose-gray max-w-none">
                    <p>
                      Nous nous engageons à fournir des services de qualité,
                      conformes aux standards professionnels et aux réglementations
                      en vigueur au Cameroun et dans la sous-région CEMAC.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Responsabilité
                  </h2>
                  <div className="prose prose-gray max-w-none">
                    <p>
                      Notre responsabilité est limitée aux prestations
                      contractuellement définies. Nous ne saurions être tenus
                      responsables des dommages indirects ou de la perte
                      d'exploitation.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Conditions de paiement
                  </h2>
                  <div className="prose prose-gray max-w-none">
                    <p>
                      Les conditions de paiement sont définies dans chaque devis ou
                      contrat de service. Les délais de paiement sont généralement
                      de 30 jours à réception de la facture.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Résiliation
                  </h2>
                  <div className="prose prose-gray max-w-none">
                    <p>
                      Toute résiliation de contrat doit être notifiée par écrit.
                      Les conditions de résiliation sont détaillées dans le contrat
                      de service.
                    </p>
                  </div>
                </section>
              </>
            )}

            {/* Contact Section - Common to all */}
            <section className="border-t border-gray-200 pt-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact</h2>
              <p className="text-gray-600">
                Pour toute question concernant ces mentions ou notre politique,
                n'hésitez pas à nous contacter :
              </p>
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p>
                  <strong>Email:</strong> {company.email}
                </p>
                <p>
                  <strong>Téléphone:</strong> {company.phone}
                </p>
                <p>
                  <strong>Adresse:</strong> {company.address}
                </p>
              </div>
            </section>
          </div>

          <div className="mt-8 pt-8 border-t border-gray-200 text-sm text-gray-500 text-center">
            <p>
              Dernière mise à jour: {new Date().toLocaleDateString('fr-FR')}
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Legal;